import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Star, UserPlus, Eye, X, ArrowRight, TrendingUp } from 'lucide-react';
import { medalClass, numEs } from '../../lib/utils';
import { karmaAPI } from '../../lib/api';
import { karmaNextStep } from '../../lib/karma';

/**
 * Top Karma, extracted out of ModernHero.
 *
 * It used to render inside the hero, which put a three-name leaderboard above the fold on a
 * municipal app and separated it by half a page from Reportar and Vecinos — the two things it
 * actually rewards. Read on its own up there it looks like vanity. Read directly under the
 * actions it scores, it reads as the other half of a loop: here is what the city asks of you,
 * here is what you get for it.
 *
 * Three things make it a board somebody checks twice rather than a plaque:
 *
 * - **Five names, not three.** With ~950 registered neighbours a top three is a closed shop; the
 *   fourth and fifth places are where the board stops being other people and starts being
 *   reachable.
 * - **What moved today.** Totals alone render the same picture every morning. "+12 hoy" is the
 *   part that makes it today's board, and it is why a board is worth opening at all.
 * - **The next step, not the podium.** The old copy told everyone the same thing: how far they
 *   were from the top 3. Against leaders in the hundreds that is accurate and useless — it names
 *   a target most people will never reach and says nothing about the one they could reach this
 *   afternoon. /karma/me returns the gap to the person immediately above instead, so the ask is
 *   the smallest real one: seven points to go up a place. It names that person only when they
 *   are in the public top ten, as the privacy policy promises (lib/karma.karmaNextStep).
 */
export function KarmaLeaderboard({ karmaLeaderboard, karmaTotals, user, onShowAuth }) {
  const navigate = useNavigate();
  const [showKarmaInfo, setShowKarmaInfo] = useState(false);
  const [standing, setStanding] = useState(null);

  // Personal standing is a separate call on purpose — see karmaAPI.me. Signed-out visitors never
  // make it, and a failure leaves the board exactly as it is rather than breaking it.
  useEffect(() => {
    let alive = true;
    if (!user) { setStanding(null); return undefined; }
    karmaAPI.me()
      .then((res) => { if (alive) setStanding(res.data || null); })
      .catch(() => { /* the board is still useful without it */ });
    return () => { alive = false; };
  }, [user]);

  if (!(karmaLeaderboard?.length >= 3)) return null;

  const board = karmaLeaderboard.slice(0, 5);
  const todayTotal = Number(karmaTotals?.today) || 0;
  const step = karmaNextStep(standing?.next);

  return (
    <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden mt-2"
      data-testid="karma-leaderboard">
      <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5 border-b border-slate-100">
        <span className="text-[11px] font-bold text-slate-700 flex items-center gap-1.5 uppercase tracking-wider">
          <Trophy size={13} className="text-amber-500" /> Top Karma
        </span>
        <div className="flex items-center gap-2">
          {todayTotal > 0 && (
            <span className="text-[11px] font-bold text-emerald-600 flex items-center gap-1"
              data-testid="karma-today-total">
              <TrendingUp size={11} /> +{numEs(todayTotal)} hoy
            </span>
          )}
          <button onClick={() => setShowKarmaInfo((v) => !v)}
            aria-expanded={showKarmaInfo}
            className="text-[11px] text-slate-400 hover:text-slate-600 transition-colors flex items-center gap-0.5">
            {showKarmaInfo ? <X size={10} /> : <Eye size={10} />} {showKarmaInfo ? 'cerrar' : '¿cómo funciona?'}
          </button>
        </div>
      </div>
      {showKarmaInfo && (
        <div className="px-3 py-2 bg-amber-50/50 border-b border-amber-100/50 text-[11px] text-slate-600 leading-relaxed">
          <p className="font-bold text-amber-700 mb-0.5">¿Qué es el karma?</p>
          <p>Puntos que ganas participando en tu ciudad: reportar incidencias, ayudar vecinos, completar tu perfil, y más. Cuanto más karma, más confianza en la comunidad.</p>
        </div>
      )}
      <div className="flex flex-col">
        {board.map((u, i) => {
          const isMe = user?.nombre && u.nombre === user.nombre;
          const today = Number(u.today) || 0;
          return (
            <button key={u.nombre || i} onClick={() => navigate('/vecinos')}
              className={`flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 transition-colors cursor-pointer border-b border-slate-50 last:border-b-0 ${
                isMe ? 'bg-torre-teal/5' : i === 0 ? 'bg-amber-50/60' : 'bg-white'
              }`}
              data-testid={`karma-rank-${i + 1}`}>
              <span className={`font-black text-base min-w-[1.25rem] shrink-0 text-center ${medalClass(i)}`}>{i + 1}</span>
              <span className="text-xs font-medium text-slate-700 truncate flex-1 text-left">
                {u.nombre}{isMe && <span className="ml-1 text-[10px] font-bold text-torre-teal">· tú</span>}
              </span>
              {today > 0 && (
                <span className="text-[10px] font-bold text-emerald-600 shrink-0" data-testid={`karma-today-${i + 1}`}>
                  +{numEs(today)} hoy
                </span>
              )}
              <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 border border-amber-200 flex items-center gap-1 shrink-0">
                <Star size={9} className="text-amber-500 fill-current" /> {numEs(u.karma)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Your own standing, and only ever the next step up.
          The first version of this congratulated anyone appearing on the board — which, tested
          against four ranked neighbours, told the person in last place "estás en el top 4 de 4".
          Being on the board is not the achievement; having somebody above you is the useful fact,
          whether you are 4th of 4 or 2nd of 947. So: if anyone is ahead, the gap (and their name
          when they are in the public top ten). Only an actual first place gets a congratulation,
          because only then is there no step. */}
      {user && standing && step && (
        <p className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100" data-testid="karma-gap">
          Vas <strong className="text-slate-700">#{numEs(standing.position)}</strong> de {numEs(standing.ranked)} en la clasificación
          {' · '}{step.lead} <strong className="text-torre-teal">{step.amount}</strong> {step.goal}
          {standing.today > 0 && <span className="text-emerald-600 font-bold"> · +{numEs(standing.today)} hoy</span>}
        </p>
      )}
      {user && standing && !standing.next && standing.position === 1 && (
        <p className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100" data-testid="karma-gap">
          Vas <strong className="text-torre-teal">primero</strong> de {numEs(standing.ranked)} en la clasificación
          {standing.today > 0 && <span className="text-emerald-600 font-bold"> · +{numEs(standing.today)} hoy</span>}
        </p>
      )}

      {/* Out of the ranking at their own request (Perfil) or an admin's: no place to report. */}
      {user && standing?.hidden && (
        <p className="px-3 py-1.5 text-[11px] text-slate-500 border-t border-slate-100" data-testid="karma-hidden">
          No apareces en la clasificación. Puedes cambiarlo en tu perfil; tus puntos se conservan.
        </p>
      )}

      {!user ? (
        <button onClick={onShowAuth}
          className="w-full flex items-center justify-center gap-2 bg-torre-teal hover:bg-teal-700 text-white py-2 px-4 text-[11px] font-bold transition-colors"
          data-testid="karma-cta">
          <UserPlus size={13} /> Únete y gana puntos karma
        </button>
      ) : (
        <button onClick={() => navigate('/vecinos')}
          className="w-full flex items-center justify-center gap-1.5 bg-slate-50 hover:bg-slate-100 text-torre-teal py-1.5 px-4 text-[11px] font-bold transition-colors"
          data-testid="karma-cta-logged">
          Gana más karma <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}
